// ABOUTME: Oracle Mystical Mappings - Complete variable conversion system for rich mystical context
// ABOUTME: Converts numerical oracle selections (1-12, 1-9, 1-4) into full mystical descriptions for Claude API

/*
 * THE HEART OF THE ORACLE SYSTEM - Understanding This File
 *
 * This file is the KEY translation layer between user interface and Claude API.
 * It transforms abstract numbers into rich mystical Portuguese descriptions that give Claude meaningful context.
 *
 * WHY THIS FILE EXISTS:
 * - Users select numbers (Ray 3, Type 2, Element 1) via UI
 * - Claude API needs mystical context, not raw numbers
 * - This file bridges that gap with rich Portuguese descriptions
 *
 * MAIN CONVERSION PROCESS:
 * 1. User selects: raySelection=3, diagnosisType=2, diagnosisElement=1
 * 2. convertOracleVariablesToMystical() converts to:
 *    - rayName: "AMOR (Relacionamentos) - Conexão emocional..."
 *    - diagnosisType: "Emoções e Sentimentos - padrões emocionais..."
 *    - diagnosisElement: "Fogo - Ação e Paixão (intensifica através de queimaduras emocionais...)"
 * 3. These get inserted into Claude API prompts instead of numbers
 *
 * EXAMPLE TRANSFORMATION:
 * Input: { raySelection: 3, userQuestion: "Como melhorar relacionamento?", diagnosisType: 2, diagnosisElement: 1 }
 * Output: {
 *   rayName: "AMOR (Relacionamentos) - Conexão emocional e relacionamentos, vibração de conexão do coração e harmonia",
 *   userQuestion: "Como melhorar relacionamento?",
 *   diagnosisType: "Emoções e Sentimentos - padrões emocionais criando desequilíbrio através de sobrecarga emocional, sentimentos reprimidos e reações emocionais intensas",
 *   diagnosisElement: "Fogo - Ação e Paixão (intensifica através de queimaduras emocionais, lutas internas, reações explosivas e impulsos descontrolados)"
 * }
 *
 * EDITING GUIDELINES:
 * - To improve Claude responses: Edit the descriptions below
 * - Too harsh responses? Soften the language in diagnosticEffect
 * - Need more mystical depth? Enhance the ray descriptions
 * - Wrong tone? Adjust the Portuguese phrasing
 *
 * KEY INSIGHT: Elements have DIFFERENT meanings for diagnosis vs solution!
 * - Diagnosis context: Shows how element CREATES problems
 * - Solution context: Shows how element PROVIDES solutions
 * - Same element, opposite effects - this is intentional design
 */

const oracleMysticalMappings = {
  
  // 12 COSMIC RAYS (Variable A: raySelection 1-12)
  // These are the primary energetic themes that guide the entire oracle reading
  // Format: "KEYWORD (Category) - Full description, vibração de [energy description]"
  // Edit these to change how Claude interprets different ray energies
  rays: {
    1: "PODER (Poder Pessoal) - Liderança interior e autonomia, vibração de autoridade pessoal e força interior",
    2: "SABEDORIA (Conhecimento) - Conhecimento profundo e discernimento, vibração de clareza do conhecimento",
    3: "AMOR (Relacionamentos) - Conexão emocional e relacionamentos, vibração de conexão do coração e harmonia",
    4: "CRIATIVIDADE (Manifestação) - Poder de manifestar e criar, capacidade de trazer ideias ao mundo material",
    5: "CURA (Vitalidade) - Vitalidade regenerativa e bem-estar, vibração de restauração e regeneração da vitalidade",
    6: "PAZ (Serenidade) - Serenidade interior e harmonia, vibração de serenidade e calma interior",
    7: "TRANSMUTAÇÃO (Alquimia) - Alquimia pessoal e transformação, vibração de mudança alquímica e transformação de padrões",
    8: "CLAREZA (Discernimento) - Visão cristalina e discernimento, vibração de precisão mental e insight claro",
    9: "HARMONIA (Equilíbrio) - Equilíbrio universal e estabilidade, vibração de equilíbrio universal e estabilidade",
    10: "FINANCEIRO (Abundância) - Prosperidade material e recursos, vibração de fluxo de prosperidade e magnetismo de recursos",
    11: "META (Propósito) - Missão de vida e direcionamento, vibração de direção de vida e alinhamento da missão",
    12: "SAÚDE (Bem-estar) - Vitalidade plena e bem-estar integral, vibração de integração completa e totalidade"
  },

  // 9 DIAGNOSIS TYPES (Variable C: diagnosisType 1-9)
  // These describe Enneagram-based fear patterns creating blockages
  // Format: Direct description of the fear pattern and its manifestations
  // Used in blockage analysis to identify root psychological patterns
  diagnosisTypes: {
    1: "medo de ser imperfeito, corrupto ou estar errado, manifestando-se através de autocrítica constante, julgamento severo dos outros, rigidez mental, raiva reprimida, necessidade de corrigir e controlar tudo, e grande receio de cometer erros",
    2: "medo de não ser digno de amor, manifestando-se através de comportamento para agradar sempre, abandono das próprias necessidades, oferta de ajuda esperando gratidão em troca, ressentimento escondido, e necessidade de ser indispensável para os outros",
    3: "medo de não ter valor próprio além de suas conquistas, manifestando-se através de obsessão pela própria imagem, dependência de aprovação e performance, desconexão do eu verdadeiro, competição intensa com outros, e grande receio de ser considerado sem valor",
    4: "medo de não ter identidade ou significado pessoal, manifestando-se através de foco constante nas próprias diferenças, melancolia frequente, sensação de que sempre falta algo, comparação dolorosa com outros, e busca intensa por autenticidade",
    5: "medo de ser incapaz ou invadido pelas demandas do mundo, manifestando-se através de isolamento como proteção, acúmulo intenso de conhecimento e recursos, redução das próprias necessidades, e grande receio de exposição emocional",
    6: "medo de ficar sem apoio ou orientação, manifestando-se através de ansiedade constante, dúvida sobre as próprias decisões, busca por figuras de autoridade, lealdade excessiva, e receio constante de ameaças e perigos",
    7: "medo de ficar preso na dor ou limitação, manifestando-se através de fuga de realidades dolorosas, mente dispersa e inquieta, impaciência frequente, busca intensa por experiências estimulantes, e grande aversão ao sofrimento",
    8: "medo de ser dominado ou controlado pelos outros, manifestando-se através de proteção emocional rígida, necessidade de manter controle, negação da própria vulnerabilidade, reações fortes contra injustiças, e grande receio de traição",
    9: "medo de perder conexão e sofrer fragmentação interior, manifestando-se através de passividade para evitar conflitos, negligência consigo mesmo, procrastinação, e grande receio de confronto e separação dos outros"
  },

  // 9 SOLUTION TYPES (Variable E: solutionType 1-9)
  // These describe Enneagram-based integration desires creating transformation
  // Format: Direct description of the healthy desire and its manifestations
  // Used in solution analysis to reveal paths to wholeness
  solutionTypes: {
    1: "desejo de ter integridade verdadeira, manifestando-se através de sabedoria prática e discernimento, aceitação da realidade como ela é, princípios equilibrados sem rigidez, serenidade interior genuína, e capacidade de ver o bem maior com clareza",
    2: "desejo de amar e ser amado genuinamente, manifestando-se através de cuidado incondicional e generoso, reconhecimento do próprio valor inerente, nutrição emocional autêntica para si e outros, e capacidade de dar sem expectativas ou manipulação",
    3: "desejo de se sentir valioso por quem é, manifestando-se através de autenticidade genuína sem máscaras, sucesso que flui naturalmente das qualidades reais, autoestima baseada no ser e não apenas no fazer, e inspiração que motiva outros pelo exemplo",
    4: "desejo de encontrar sua verdadeira identidade, manifestando-se através de autenticidade emocional profunda, criatividade que transforma experiências pessoais, capacidade de processar todas as emoções com equilíbrio, e renovação constante através da autoexpressão",
    5: "desejo de sentir-se capaz e competente, manifestando-se através de conhecimento profundo aplicado com sabedoria, confiança para compartilhar insights valiosos, participação equilibrada no mundo, e contribuição expertise com generosidade",
    6: "desejo de ter segurança verdadeira, manifestando-se através de coragem baseada em confiança interior, orientação interna confiável, lealdade saudável sem dependência, e capacidade de enfrentar desafios com fé e determinação",
    7: "desejo de ser feliz e pleno, manifestando-se através de alegria genuína e contagiante, foco profundo quando necessário, capacidade de encontrar satisfação no presente, e entusiasmo equilibrado que inspira e eleva outros",
    8: "desejo de proteger a si mesmo e aos outros, manifestando-se através de força usada para servir e proteger, vulnerabilidade corajosa que permite conexão real, liderança compassiva e justa, e poder usado para empoderar e elevar outros",
    9: "desejo de manter paz interior e harmonia, manifestando-se através de presença curativa que une pessoas, ação decidida quando necessário, valor próprio reconhecido, e capacidade de criar harmonia respeitando todas as perspectivas"
  },

  // 4 ELEMENTS (Variables D & F: diagnosisElement/solutionElement 1-4)
  // CRITICAL: Same element has OPPOSITE meanings in diagnosis vs solution contexts!
  //
  // DIAGNOSIS CONTEXT: How the element INTENSIFIES problems
  // SOLUTION CONTEXT: How the element STRENGTHENS solutions
  //
  // This dual nature creates nuanced readings where Fire can both:
  // - Cause "explosive reactions" (diagnosis) AND provide "transformative energy" (solution)
  //
  // The getElementDescription() function automatically selects the right context
  elements: {
    1: {
      name: "Elemento Fogo",
      diagnosticEffect: "intensifica os bloqueios através de impulsos descontrolados, reações explosivas e súbitas, raiva que transborda facilmente, e paixões que dominam a razão",
      solutionEffect: "amplifica o desbloqueio através de energia vital direcionada e propósito claro, coragem para tomar decisões transformadoras, paixão equilibrada que motiva mudanças positivas, e força interior que supera obstáculos com determinação"
    },
    2: {
      name: "Elemento Água",
      diagnosticEffect: "intensifica os bloqueios através de ondas emocionais intensas, sensibilidade exagerada a tudo, turbulência sentimental constante, e vulnerabilidade emocional que paralisa",
      solutionEffect: "amplifica o desbloqueio através de fluxo emocional saudável e renovador, intuição clara que orienta escolhas sábias, sensibilidade equilibrada que conecta com compaixão genuína, e flexibilidade que permite adaptação natural às mudanças"
    },
    3: {
      name: "Elemento Terra",
      diagnosticEffect: "intensifica os bloqueios através de rigidez mental e comportamental, teimosia que impede qualquer mudança, estagnação pesada em padrões antigos, e resistência total ao novo",
      solutionEffect: "amplifica o desbloqueio através de estabilidade que sustenta mudanças duradouras, perseverança constante em direção aos objetivos, estruturas sólidas que apoiam crescimento pessoal, e capacidade de manifestar intenções em resultados concretos"
    },
    4: {
      name: "Elemento Ar",
      diagnosticEffect: "intensifica os bloqueios através de turbulência mental constante, pensamentos acelerados que não param, ansiedade mental paralisante, e comunicação confusa e dispersa",
      solutionEffect: "amplifica o desbloqueio através de clareza mental que dissolve confusões, comunicação clara e objetiva, perspectiva ampla que enxerga soluções criativas, e leveza mental que permite renovação de pensamentos e ideias"
    }
  },

  // 9 TYPE NAMES for UI Display (corresponds to diagnosisTypes/solutionTypes)
  typeNames: {
    1: "O REFORMADOR",
    2: "O AUXILIADOR",
    3: "O REALIZADOR",
    4: "O INDIVIDUALISTA",
    5: "O INVESTIGADOR",
    6: "O LEAL",
    7: "O ENTUSIASTA",
    8: "O DESAFIADOR",
    9: "O PACIFICADOR"
  },

  // RAY SHORT NAMES for UI Display (extract just the main keyword)
  rayShortNames: {
    1: "PODER",
    2: "SABEDORIA",
    3: "AMOR",
    4: "CRIATIVIDADE",
    5: "CURA",
    6: "PAZ",
    7: "TRANSMUTAÇÃO",
    8: "CLAREZA",
    9: "HARMONIA",
    10: "FINANCEIRO",
    11: "META",
    12: "SAÚDE"
  },

  // ELEMENT SHORT NAMES for UI Display (extract just the element name)
  elementShortNames: {
    1: "Fogo",
    2: "Água",
    3: "Terra",
    4: "Ar"
  }
};

/*
 * MAIN CONVERSION FUNCTION - The Heart of the Translation System
 *
 * This is THE function that converts raw user selections into mystical context for Claude API.
 * Called by claudeAPI.js before every API call to transform numerical data into rich descriptions.
 *
 * INPUT: Raw oracle data object with numerical selections
 * OUTPUT: Mystical context object with full Portuguese descriptions
 *
 * Flow: UI numbers → this function → mystical descriptions → Claude API prompts
 */

// Oracle Variable Converter - Main Function
export const convertOracleVariablesToMystical = (oracleData) => {
  return {
    rayName: oracleMysticalMappings.rays[oracleData.raySelection],
    userQuestion: oracleData.userQuestion,
    diagnosisType: oracleMysticalMappings.diagnosisTypes[oracleData.diagnosisType],
    diagnosisElement: getElementDescription(oracleData.diagnosisElement, 'diagnosis'),
    solutionType: oracleMysticalMappings.solutionTypes[oracleData.solutionType],
    solutionElement: getElementDescription(oracleData.solutionElement, 'solution')
  };
};

/*
 * ELEMENT CONTEXT SWITCHER - Critical Dual-Nature Handler
 *
 * This function is WHY elements can mean opposite things in diagnosis vs solution.
 * It automatically selects diagnosticEffect vs solutionEffect based on context parameter.
 *
 * EXAMPLES:
 * - Fire + diagnosis = "queimaduras emocionais, reações explosivas" (negative)
 * - Fire + solution = "ação motivada, energia transformadora" (positive)
 *
 * This creates sophisticated readings where same element provides both problem AND solution insights.
 */

// Element Description Helper (handles diagnostic vs solution context)
export const getElementDescription = (elementNumber, context) => {
  const element = oracleMysticalMappings.elements[elementNumber];
  if (!element) return "Elemento desconhecido";
  
  if (context === 'diagnosis') {
    return `${element.name} (${element.diagnosticEffect})`;
  } else if (context === 'solution') {
    return `${element.name} (${element.solutionEffect})`;
  } else {
    return element.name;
  }
};

// Quick Ray Name Getter (for simple ray references - backward compatibility)
export const getRayName = (rayNumber) => {
  return oracleMysticalMappings.rays[rayNumber] || "Raio desconhecido";
};

// UI Display Helper Functions
export const getRayDisplayName = (rayNumber) => {
  return oracleMysticalMappings.rayShortNames[rayNumber] || `Raio ${rayNumber}`;
};

export const getTypeDisplayName = (typeNumber) => {
  return oracleMysticalMappings.typeNames[typeNumber] || `Tipo ${typeNumber}`;
};

export const getElementDisplayName = (elementNumber) => {
  return oracleMysticalMappings.elementShortNames[elementNumber] || `Elemento ${elementNumber}`;
};

// Validation Helper
export const validateOracleInputs = (oracleData) => {
  const errors = [];
  
  if (!oracleMysticalMappings.rays[oracleData.raySelection]) {
    errors.push(`Invalid ray selection: ${oracleData.raySelection}`);
  }
  if (!oracleMysticalMappings.diagnosisTypes[oracleData.diagnosisType]) {
    errors.push(`Invalid diagnosis type: ${oracleData.diagnosisType}`);
  }
  if (!oracleMysticalMappings.elements[oracleData.diagnosisElement]) {
    errors.push(`Invalid diagnosis element: ${oracleData.diagnosisElement}`);
  }
  if (!oracleMysticalMappings.solutionTypes[oracleData.solutionType]) {
    errors.push(`Invalid solution type: ${oracleData.solutionType}`);
  }
  if (!oracleMysticalMappings.elements[oracleData.solutionElement]) {
    errors.push(`Invalid solution element: ${oracleData.solutionElement}`);
  }
  
  return errors;
};

/*
 * OPTIMIZATION NOTES FOR FUTURE EDITING:
 *
 * TO IMPROVE CLAUDE API RESPONSES, EDIT THE DESCRIPTIONS ABOVE:
 *
 * 1. RESPONSE TOO HARSH/NEGATIVE?
 *    → Soften language in diagnosticEffect descriptions (lines 52, 57, 62, 67)
 *    → Use gentler terms like "desafios" instead of "lutas"
 *
 * 2. RESPONSE LACKS MYSTICAL DEPTH?
 *    → Enhance ray descriptions with more spiritual language
 *    → Add more "vibração de" details
 *
 * 3. RESPONSE TONE NOT RIGHT?
 *    → Adjust Portuguese phrasing to be more/less formal
 *    → Change from "através de" to "por meio de" for variety
 *
 * 4. ELEMENT COMBINATIONS NOT WORKING?
 *    → Balance diagnosticEffect vs solutionEffect language
 *    → Ensure they feel like opposite sides of same energy
 *
 * 5. TESTING CHANGES:
 *    → Use Claude Console with exact prompts from buildPrompt() in claudeAPI.js
 *    → Test different ray+element combinations
 *    → Verify Portuguese sounds natural and mystical
 *
 * REMEMBER: This file directly controls Claude's understanding of oracle energies!
 */

// <-- edit here for mystical mappings: All oracle descriptions can be tweaked here for better AI responses

export default oracleMysticalMappings;